import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip } from "recharts";
import { api } from "@/lib/api";
import { AiUsageCard } from "@/components/AiUsageCard";
import {
  VideoCamera, Warning, UsersThree, Timer, ShieldWarning,
  TrendUp, Check, ShoppingBag, ChartLineUp,
} from "@phosphor-icons/react";

const SEV = {
  critical: "bg-[#FF3B30]/15 text-[#FF3B30] border-[#FF3B30]/40",
  high: "bg-[#FF9F0A]/15 text-[#FF9F0A] border-[#FF9F0A]/40",
  medium: "bg-[#0A84FF]/15 text-[#0A84FF] border-[#0A84FF]/40",
  low: "bg-[#71717A]/15 text-[#A1A1AA] border-[#71717A]/40",
};
const THREAT = { high: "#FF3B30", medium: "#FF9F0A", low: "#0A84FF", none: "#32D74B" }; // eslint-disable-line no-unused-vars


const TREND_PERIODS = [
  { id: "day", label: "Daily" },
  { id: "week", label: "Weekly" },
  { id: "month", label: "Monthly" },
];

function TrendsPanel() {
  const [gran, setGran] = useState("day");
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get(`/analytics/trends?granularity=${gran}`)
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData(null); });
    return () => { alive = false; };
  }, [gran]);

  const buckets = data?.buckets || [];
  const totals = data?.totals || {};
  const hasData = buckets.some((b) => b.walk_in > 0 || b.customers > 0);
  const active = buckets.filter((b) => b.walk_in > 0);
  const avgWalkIn = active.length ? Math.round(active.reduce((s, b) => s + b.walk_in, 0) / active.length) : 0;
  const unit = { day: "Day", week: "Week", month: "Month" }[gran];

  return (
    <div className="mt-4 bg-[#121214] border border-white/10 p-4" data-testid="footfall-trends-panel">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h2 className="font-display font-black uppercase tracking-tighter text-sm flex items-center gap-2">
          <UsersThree size={16} weight="bold" className="text-[#BF5AF2]" /> Walk-In Trends
        </h2>
        <div className="flex items-center border border-white/10" data-testid="trend-period-toggle">
          {TREND_PERIODS.map((p) => (
            <button
              key={p.id}
              data-testid={`trend-period-${p.id}`}
              onClick={() => setGran(p.id)}
              className={`px-3 py-1 text-[11px] font-mono-data uppercase tracking-wider transition-colors ${
                gran === p.id ? "bg-[#BF5AF2] text-black" : "text-[#A1A1AA] hover:text-white"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-3">
        <div data-testid="trend-total-walkins">
          <div className="text-[10px] uppercase tracking-widest text-[#71717A]">Total Walk-Ins</div>
          <div className="font-mono-data text-2xl text-[#BF5AF2]">{totals.walk_in ?? 0}</div>
        </div>
        <div data-testid="trend-avg-walkins">
          <div className="text-[10px] uppercase tracking-widest text-[#71717A]">Avg Walk-Ins / {unit}</div>
          <div className="font-mono-data text-2xl text-[#0A84FF]">{avgWalkIn}</div>
        </div>
        <div data-testid="trend-peak">
          <div className="text-[10px] uppercase tracking-widest text-[#71717A]">Peak Occupancy</div>
          <div className="font-mono-data text-2xl text-[#32D74B]">{totals.peak ?? 0}</div>
        </div>
      </div>

      {hasData ? (
        <ResponsiveContainer width="100%" height={180}>
          <BarChart data={buckets} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#27272A" vertical={false} />
            <XAxis dataKey="label" tick={{ fill: "#71717A", fontSize: 10, fontFamily: "IBM Plex Mono" }} axisLine={{ stroke: "#27272A" }} tickLine={false} interval="preserveStartEnd" />
            <YAxis tick={{ fill: "#71717A", fontSize: 10, fontFamily: "IBM Plex Mono" }} axisLine={false} tickLine={false} allowDecimals={false} />
            <Tooltip
              cursor={{ fill: "rgba(191,90,242,0.08)" }}
              contentStyle={{ background: "#1A1A1E", border: "1px solid #27272A", borderRadius: 0, fontFamily: "IBM Plex Mono", fontSize: 12 }}
            />
            <Bar dataKey="walk_in" name="Walk-Ins" fill="#BF5AF2" radius={[2, 2, 0, 0]} maxBarSize={38} />
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <div className="text-xs text-[#71717A] font-mono-data py-8 text-center">
          No walk-in history yet — counts accumulate as the edge agent streams door-line data.
        </div>
      )}
    </div>
  );
}

function Kpi({ icon: Icon, label, value, sub, accent }) {
  return (
    <div className="bg-[#121214] border border-white/10 p-4" data-testid={`kpi-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs uppercase tracking-widest text-[#71717A]">{label}</span>
        <Icon size={18} weight="bold" style={{ color: accent }} />
      </div>
      <div className="font-mono-data font-medium text-3xl tracking-tight">{value}</div>
      {sub && <div className="text-xs text-[#71717A] mt-1 font-mono-data">{sub}</div>}
    </div>
  );
}

function CameraHealthTile({ cam, i }) {
  const src = cam.frame_b64 ? `data:image/jpeg;base64,${cam.frame_b64}` : cam.image;
  const online = cam.live || cam.status === "online";
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: i * 0.03 }}
      data-testid={`camera-health-${cam.id}`}
      className="relative aspect-video bg-black border border-white/10 overflow-hidden"
    >
      {src
        ? <img src={src} alt={cam.name} className="w-full h-full object-cover opacity-55" />
        : <div className="w-full h-full flex items-center justify-center text-[#3f3f46]"><VideoCamera size={26} weight="thin" /></div>}
      <div className="absolute inset-0 bg-black/40" />
      <div className="absolute top-1.5 left-1.5 flex items-center gap-1.5">
        <span className="w-1.5 h-1.5 rounded-full" style={{ background: online ? "#32D74B" : "#71717A", boxShadow: online ? "0 0 6px #32D74B" : "none" }} />
        <span className="font-mono-data text-[9px] tracking-widest uppercase" style={{ color: online ? "#32D74B" : "#71717A" }}>
          {online ? "Online" : "Offline"}
        </span>
      </div>
      {cam.people_now != null && (
        <span data-testid={`camera-people-count-${cam.id}`} className="absolute top-1.5 right-1.5 flex items-center gap-1 font-mono-data text-[9px] px-1 py-0.5 bg-[#BF5AF2] text-white uppercase tracking-wider">
          <UsersThree size={10} weight="bold" /> {cam.people_now}
        </span>
      )}
      <div className="absolute bottom-0 left-0 right-0 px-1.5 py-1 bg-gradient-to-t from-black/95 to-transparent">
        <p className="text-[11px] font-medium leading-none truncate">{cam.name}</p>
        <p className="font-mono-data text-[9px] text-[#71717A] mt-0.5 truncate">{cam.zone || "—"}</p>
      </div>
    </motion.div>
  );
}

export default function Dashboard() {
  const [summary, setSummary] = useState(null);
  const [live, setLive] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [hourly, setHourly] = useState([]);

  const load = () => {
    api.get("/dashboard/summary").then((r) => setSummary(r.data)).catch(() => {});
    api.get("/dashboard/live").then((r) => setLive(r.data)).catch(() => {});
    api.get("/alerts").then((r) => setAlerts(r.data)).catch(() => {});
    api.get("/interactions").then((r) => setHourly(r.data)).catch(() => {});
  };
  useEffect(() => { load(); const t = setInterval(load, 6000); return () => clearInterval(t); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const resolve = async (id) => {
    await api.post(`/alerts/${id}/resolve`);
    setAlerts((prev) => prev.map((a) => (a.id === id ? { ...a, status: "resolved" } : a)));
    toast.success("Alert resolved");
  };

  const openAlerts = alerts.filter((a) => a.status === "open");
  const anyLive = live.some((c) => c.live);

  return (
    <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
      {/* KPIs */}
      <div className="xl:col-span-12 grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-4">
        <Kpi icon={VideoCamera} label="Feeds Online" value={summary ? `${summary.cameras_online}/${summary.cameras_total}` : "—"} sub="Active streams" accent="#32D74B" />
        <Kpi icon={ShieldWarning} label="Open Alerts" value={summary?.open_alerts ?? "—"} sub={`${summary?.critical_alerts ?? 0} critical`} accent="#FF3B30" />
        <Kpi icon={TrendUp} label="Avg Productivity" value={summary ? `${summary.avg_productivity}%` : "—"} sub={`${summary?.active_staff ?? 0} staff on floor`} accent="#007AFF" />
        <Kpi icon={ShoppingBag} label="Customers Today" value={summary?.customers_today ?? "—"} sub={summary?.walk_ins_today ? `${summary.walk_ins_today} door walk-ins` : `${summary?.unique_customers_today ?? 0} unique seen`} accent="#0A84FF" />
        <Kpi icon={Timer} label="Avg Wait" value={summary ? `${summary.avg_wait_seconds}s` : "—"} sub="Checkout queue" accent="#FF9F0A" />
        <AiUsageCard />
        <Kpi icon={UsersThree} label="People Now" value={summary?.people_now ?? "—"} sub={`${summary?.entered_today ?? 0} in today`} accent="#BF5AF2" />
      </div>

      {/* Camera health (analytics-only — live video is on your DVR/TV) */}
      <div className="xl:col-span-9">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display font-black uppercase tracking-tighter text-sm flex items-center gap-2">
            <VideoCamera size={16} weight="bold" className="text-[#007AFF]" /> Camera Health
          </h2>
          <span className="font-mono-data text-xs flex items-center gap-1.5" style={{ color: anyLive ? "#32D74B" : "#71717A" }}>
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: anyLive ? "#32D74B" : "#71717A" }} />
            {live.filter((c) => c.live || c.status === "online").length}/{live.length} online
          </span>
        </div>
        {live.length === 0 ? (
          <div className="text-sm text-[#71717A] font-mono-data p-6 border border-white/10">No cameras connected yet.</div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 2xl:grid-cols-4 gap-2.5" data-testid="camera-health-grid">
            {live.map((cam, i) => <CameraHealthTile key={cam.id} cam={cam} i={i} />)}
          </div>
        )}

        {/* Busy-day foot traffic */}
        <div className="mt-4 bg-[#121214] border border-white/10 p-4" data-testid="foot-traffic-panel">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display font-black uppercase tracking-tighter text-sm flex items-center gap-2">
              <ChartLineUp size={16} weight="bold" className="text-[#0A84FF]" /> Customer Interactions · Today
            </h2>
            <span className="font-mono-data text-[10px] text-[#71717A]">{summary?.samples_today ?? 0} AI reads</span>
          </div>
          {hourly.length ? (
            <ResponsiveContainer width="100%" height={140}>
              <AreaChart data={hourly}>
                <defs>
                  <linearGradient id="dInt" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0A84FF" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#0A84FF" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="hour" tick={{ fill: "#71717A", fontSize: 10, fontFamily: "IBM Plex Mono" }} axisLine={{ stroke: "#27272A" }} tickLine={false} />
                <Tooltip contentStyle={{ background: "#1A1A1E", border: "1px solid #27272A", borderRadius: 0, fontFamily: "IBM Plex Mono", fontSize: 12 }} />
                <Area type="monotone" dataKey="count" name="Interactions" stroke="#0A84FF" strokeWidth={2} fill="url(#dInt)" />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-xs text-[#71717A] font-mono-data py-8 text-center">No interactions recorded yet today — data appears as the AI analyzes live frames.</div>
          )}
        </div>

        <TrendsPanel />
      </div>

      {/* Alerts sidebar */}
      <div className="xl:col-span-3">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display font-black uppercase tracking-tighter text-sm">Real-Time Alerts</h2>
          <span data-testid="open-alert-count" className="font-mono-data text-xs px-2 py-0.5 bg-[#FF3B30]/15 text-[#FF3B30] border border-[#FF3B30]/40">
            {openAlerts.length} OPEN
          </span>
        </div>
        <div className="space-y-2">
          {openAlerts.length === 0 && (
            <div className="text-sm text-[#71717A] font-mono-data p-4 border border-white/10">No open alerts. All clear.</div>
          )}
          {openAlerts.map((a, i) => (
            <motion.div
              key={a.id}
              initial={{ opacity: 0, x: 12 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.06 }}
              data-testid={`alert-${a.id}`}
              className="bg-[#121214] border border-white/10 p-3"
            >
              <div className="flex items-center justify-between mb-1.5">
                <span className={`font-mono-data text-[10px] px-1.5 py-0.5 border uppercase tracking-wider ${SEV[a.severity]}`}>{a.severity}</span>
                <span className="font-mono-data text-[10px] text-[#71717A]">{new Date(a.ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
              </div>
              <p className="text-sm font-medium">{a.type}</p>
              <p className="text-xs text-[#A1A1AA] mt-0.5 leading-snug">{a.message}</p>
              <div className="flex items-center justify-between mt-2">
                <span className="font-mono-data text-[10px] text-[#71717A]">{a.camera}</span>
                <button
                  data-testid={`alert-resolve-${a.id}`}
                  onClick={() => resolve(a.id)}
                  className="flex items-center gap-1 text-[10px] font-mono-data uppercase tracking-wider text-[#32D74B] hover:text-white transition-colors"
                >
                  <Check size={12} weight="bold" /> Resolve
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}
